import React, { useMemo } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import StackedBarChart from '../../atoms/StackedBarChart/StackedBarChart';
import ChartNoDataState from '../../atoms/ChartNoDataState/ChartNoDataState';
import Select from '../../atoms/Select/Select';
import Button from '../../atoms/Button/Button';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import { ChartConfig } from '../../../utils/BaseChart/BaseChartOptions';
import {
    formatNumWithKMB,
    formatNumWithKMBTwoDecimalPlaces,
    formatXAxisLabel,
    CHART_FONT_FAMILY_CSS,
} from '../../../utils/formatters';
import {
    getAggregatedChartTooltipInteraction,
    getChartColumnHighlightInteraction,
} from '../../../utils/chartAggregatedTooltip';
import { useStackedBarChartFilters, type StackedBarChartRow } from './useStackedBarChartFilters';
import { EnhancedStackedBarChartProps } from './EnhancedStackedBarChart.types';
import ChartFilterBar, { ChartFilterColumn, CHART_FILTER_SELECT_CLASSNAME } from '../EnhancedBarChart/ChartFilterBar';
import { chartMultiSelectTagRender } from '../EnhancedBarChart/chartFilterMultiTagRender';

type StackedBarChartRowWithTotals = StackedBarChartRow & {
    stackTotal: number;
    showTotalLabel: boolean;
    /** When true, total label is placed below the stack (negative total); otherwise above (positive / zero). */
    totalMarkerBelow: boolean;
};

/** Add per-row stack total and which segment anchors the total label (one label per category). */
function enrichRowsWithStackTotals(rows: StackedBarChartRow[]): StackedBarChartRowWithTotals[] {
    if (rows.length === 0) return [];

    const byCategory = new Map<string, StackedBarChartRow[]>();
    for (const row of rows) {
        const list = byCategory.get(row.category) ?? [];
        list.push(row);
        byCategory.set(row.category, list);
    }

    const totalByCategory = new Map<string, number>();
    const totalLabelSegmentIndexByCategory = new Map<string, number>();

    for (const [category, group] of byCategory) {
        const values = group.map((r) => r.value);
        const stackTotal = values.reduce((a, b) => a + b, 0);
        totalByCategory.set(category, stackTotal);

        let cum = 0;
        const prefixes: number[] = [];
        for (const v of values) {
            cum += v;
            prefixes.push(cum);
        }
        if (prefixes.length === 0) {
            totalLabelSegmentIndexByCategory.set(category, -1);
            continue;
        }
        // G2 stackY stacks negative segments in *reverse* series order (see stackY NG.reverse()).
        // The bar's lowest point is the bottom of the first negative segment in data order (NG[0]),
        // not the segment where a forward cumulative sum hits min(prefixes).
        let segIdx = -1;
        if (stackTotal < 0) {
            const firstNegIdx = values.findIndex((v) => v < 0);
            segIdx = firstNegIdx >= 0 ? firstNegIdx : values.length - 1;
        } else {
            const outer = Math.max(...prefixes);
            for (let i = 0; i < prefixes.length; i++) {
                if (Math.abs(prefixes[i] - outer) < 1e-9) segIdx = i;
            }
            if (segIdx < 0) segIdx = prefixes.length - 1;
        }
        totalLabelSegmentIndexByCategory.set(category, segIdx);
    }

    const indexWithinCategory = new Map<string, number>();
    return rows.map((row) => {
        const idx = indexWithinCategory.get(row.category) ?? 0;
        indexWithinCategory.set(row.category, idx + 1);
        const labelSegIdx = totalLabelSegmentIndexByCategory.get(row.category) ?? -1;
        const stackTotal = totalByCategory.get(row.category) ?? 0;
        return {
            ...row,
            stackTotal,
            totalMarkerBelow: stackTotal < 0,
            showTotalLabel: idx === labelSegIdx && labelSegIdx >= 0,
        };
    });
}

const STACK_BAR_COLORS = [
    '#5B50D1',
    '#DBC7A4',
    '#4ECDC4',
    '#FF6B6B',
    '#95E1D3',
    '#F38181',
    '#AA96DA',
    '#FCBAD3',
    '#A8D8EA',
    '#FFD93D',
];

const EnhancedStackedBarChart: React.FC<EnhancedStackedBarChartProps> = (props) => {
    const {
        vizData,
        title: propTitle,
        description,
        showFilters = true,
        showSlider = true,
        onDownload,
        onBarClick,
        sizeAttributes,
        className = '',
        chartClassName = '',
    } = props;

    const theme = useTheme();
    const chartFilters = useStackedBarChartFilters(vizData);

    const chartConfig = useMemo(() => {
        if (!vizData || !chartFilters.normalizedVizData) return null;

        const normalized = chartFilters.normalizedVizData;
        const title = normalized.chartTitle || propTitle || '';
        const yAxisLabel = Array.isArray(normalized.yAxisTitle) ? normalized.yAxisTitle[0] : '';
        const xAxisKey = Array.isArray(normalized.xAxisTitle) ? normalized.xAxisTitle[0] || 'category' : 'category';

        const legend = chartFilters.selectedZ.map((label, idx) => ({
            label,
            color: STACK_BAR_COLORS[idx % STACK_BAR_COLORS.length],
        }));

        return {
            title,
            yAxisLabel,
            xAxisKey,
            data: chartFilters.chartData,
            legend,
        };
    }, [vizData, propTitle, chartFilters.chartData, chartFilters.selectedZ, chartFilters.normalizedVizData]);

    const barsConfig: ChartConfig[] = useMemo(() => {
        if (!chartConfig || chartConfig.data.length === 0) return [];

        const colorRange = chartConfig.legend.map((l) => l.color);

        return [
            {
                encode: {
                    x: 'category',
                    y: 'value',
                    color: 'series',
                },
                scale: {
                    color: {
                        range: colorRange,
                        domain: chartConfig.legend.map((l) => l.label),
                    },
                },
                transform: [{ type: 'stackY' }],
                labels: [
                    {
                        text: (d: StackedBarChartRowWithTotals) =>
                            d.showTotalLabel && !d.totalMarkerBelow
                                ? formatNumWithKMBTwoDecimalPlaces(d.stackTotal)
                                : '',
                        position: 'top',
                        // Default 'top' uses textBaseline 'top', which draws text downward into the bar.
                        // Bottom baseline anchors the label on the stack top so the number sits above the bar.
                        textBaseline: 'bottom',
                        dy: -8,
                        fill: 'rgba(0, 0, 0, 0.65)',
                        fontSize: 12,
                        fontFamily: CHART_FONT_FAMILY_CSS,
                        transform: [{ type: 'overlapHide' }],
                    },
                    {
                        text: (d: StackedBarChartRowWithTotals) =>
                            d.showTotalLabel && d.totalMarkerBelow
                                ? formatNumWithKMBTwoDecimalPlaces(d.stackTotal)
                                : '',
                        position: 'bottom',
                        // Top baseline anchors at the stack bottom so the number sits below the bar (symmetric to positive).
                        textBaseline: 'top',
                        dy: 8,
                        fill: 'rgba(0, 0, 0, 0.65)',
                        fontSize: 12,
                        fontFamily: CHART_FONT_FAMILY_CSS,
                        transform: [{ type: 'overlapHide' }],
                    },
                ],
                ...(showSlider && {
                    additionalConfig: {
                        slider: {
                            x: {
                                selectionFill: '#F7F5FF',
                                selectionFillOpacity: 1,
                            },
                        },
                    },
                }),
                tooltip: {
                    title: (d: StackedBarChartRowWithTotals) => String(d.category ?? ''),
                    items: [
                        (d: StackedBarChartRowWithTotals) => {
                            const leg = chartConfig.legend.find((l) => l.label === d.series);
                            return {
                                name: String(d.series ?? ''),
                                value: formatNumWithKMB(d.value),
                                color: leg?.color ?? STACK_BAR_COLORS[0],
                            };
                        },
                    ],
                },
            },
        ];
    }, [chartConfig, showSlider]);

    const { data, yScaleExtension, hasNegativeStackTotals } = useMemo(() => {
        const rows = chartConfig?.data ?? [];
        const enriched = enrichRowsWithStackTotals(rows);
        const totalByCategory = new Map<string, number>();
        for (const r of enriched) {
            if (!totalByCategory.has(r.category)) totalByCategory.set(r.category, r.stackTotal);
        }
        let minStackTotal = Infinity;
        for (const t of totalByCategory.values()) minStackTotal = Math.min(minStackTotal, t);
        const hasNeg = Number.isFinite(minStackTotal) && minStackTotal < 0;
        const yExt =
            hasNeg
                ? {
                      domainMin:
                          minStackTotal - Math.max(0.04, Math.abs(minStackTotal) * 0.15),
                  }
                : {};
        return { data: enriched, yScaleExtension: yExt, hasNegativeStackTotals: hasNeg };
    }, [chartConfig?.data]);

    const viewLevelAdditionalProps = useMemo(
        () => ({
            theme: { fontFamily: CHART_FONT_FAMILY_CSS },
            scale: {
                /** Slightly more than bar chart so stack totals above the tallest stack stay inside the plot. */
                y: { padding: 0.15, ...yScaleExtension },
            },
            axis: {
                x: {
                    labelFormatter: (text: string) => formatXAxisLabel(text),
                    title: false,
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                    // Extra room between plot bottom and category labels when totals sit below bars.
                    ...(hasNegativeStackTotals && {
                        crossPadding: 78,
                        labelSpacing: 12,
                    }),
                },
                y: {
                    labelFormatter: (value: number) => formatNumWithKMB(value),
                    title: false,
                    grid: true,
                    gridLineWidth: 1,
                    gridStroke: 'rgba(0, 0, 0, 0.06)',
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                },
            },
            legend: false,
            interaction: {
                ...getChartColumnHighlightInteraction(),
                tooltip: getAggregatedChartTooltipInteraction(),
            },
            /** See EnhancedBarChart: `sliderFilter` defaults `clip` to true while sliding, which trims labels. */
            clip: false,
        }),
        [yScaleExtension, hasNegativeStackTotals],
    );

    const chartTheme = useMemo(
        () => ({
            ...theme,
            components: {
                ...theme?.components,
                Select: {
                    ...(theme as { components?: { Select?: object } })?.components?.Select,
                    colorTextPlaceholder: 'rgba(0,0,0,0.7)',
                },
            },
        }),
        [theme],
    );

    if (!vizData || !chartFilters.normalizedVizData) return null;

    const title = chartConfig?.title ?? vizData.chartTitle ?? '';
    const legend = chartConfig?.legend ?? [];
    const yAxisLabel = chartConfig?.yAxisLabel ?? '';
    const hasZFilter = chartFilters.zOptions.length > 0;

    return (
        <ConfigProvider theme={chartTheme}>
            <div
                className={`w-full bg-white rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-enhanced-stacked-barchart
            >
                {/* Header Section - same as Bar/Line chart */}
                <div className="p-5 border-b border-[rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-normal text-[rgba(0,0,0,0.88)] m-0">
                            {title}
                        </h3>
                        {onDownload && (
                            <Button
                                type="text"
                                icon={<DownloadFile size={16} />}
                                onClick={onDownload}
                                className="!p-2"
                                data-test-id={`${props['data-test-id']}-download-btn`}
                            />
                        )}
                    </div>

                    {description && (
                        <p className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] m-0">
                            {description}
                        </p>
                    )}
                </div>

                {/* Z Filter Section - only filter for stacked bar */}
                {showFilters && hasZFilter && (
                    <div className="px-5 pt-5 pb-3" data-export-exclude>
                        <ChartFilterBar>
                            <ChartFilterColumn label="Factors">
                                <Select
                                    mode="multiple"
                                    value={chartFilters.selectedZ}
                                    onChange={(value) => chartFilters.updateZFilter(value as string[])}
                                    options={chartFilters.zOptions.map((z: string) => ({
                                        label: z,
                                        value: z,
                                    }))}
                                    tagRender={chartMultiSelectTagRender(chartFilters.selectedZ, (v) => v)}
                                    className={CHART_FILTER_SELECT_CLASSNAME}
                                    placeholder="Select factors..."
                                    data-test-id={`${props['data-test-id']}-filter-z`}
                                />
                            </ChartFilterColumn>
                        </ChartFilterBar>
                    </div>
                )}

                {/* Legend Section */}
                {legend.length > 0 && (
                    <div className="px-5 py-3 w-full min-w-0">
                        <div className="flex justify-center gap-4 flex-wrap items-center max-w-full">
                            {legend.map((item) => (
                                <div
                                    key={`legend-${item.label}`}
                                    className="flex items-center gap-2 min-w-0 max-w-full"
                                >
                                    <div
                                        className="w-3 h-3 rounded-sm shrink-0"
                                        style={{ backgroundColor: item.color }}
                                    />
                                    <span className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] break-words">
                                        {item.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Y-Axis Label */}
                {yAxisLabel && (
                    <div className="px-5 pt-3">
                        <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                            {yAxisLabel}
                        </p>
                    </div>
                )}

                {/* Chart Section - same container as Bar/Line */}
                <div className={`px-5 py-5 ${chartClassName}`}>
                    {data.length === 0 || barsConfig.length === 0 ? (
                        <ChartNoDataState
                            minHeight={400}
                            data-test-id={`${props['data-test-id']}-no-data`}
                        />
                    ) : (
                        <StackedBarChart
                            data={data}
                            barsConfig={barsConfig}
                            legend={[]}
                            sizeAttributes={{
                                height: 400,
                                paddingTop: 60,
                                insetTop: 28,
                                paddingBottom:
                                    (showSlider ? 140 : 100) + (hasNegativeStackTotals ? 56 : 35),
                                paddingLeft: 80, // Reserve space for y-axis labels (e.g. "100M", "500M") to prevent truncation in constrained panels
                                ...sizeAttributes,
                            }}
                            viewLevelAdditionalProps={viewLevelAdditionalProps}
                            barClickHandler={onBarClick}
                            data-test={`${props['data-test']}-chart`}
                        />
                    )}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default EnhancedStackedBarChart;
